/**
 * DayCell — a single date cell within the calendar grid.
 * Renders card chips, handles overflow, and acts as a drop target.
 *
 * Validates: Requirements 7.1, 7.2, 7.4, 8.5, 10.1, 10.3, 10.6
 */

import React, { useCallback, useMemo } from 'react';
import { CardChip } from './CardChip';
import { OverflowIndicator } from './OverflowIndicator';
import { formatDateForAria, sortCardsForDay } from '../../utils/date-utils';
import type { CardSummary, ListSummary } from '../../state/active-board-context';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface DayCellProps {
  readonly date: string; // 'YYYY-MM-DD'
  readonly isCurrentMonth: boolean;
  readonly isToday: boolean;
  readonly cards: readonly CardSummary[];
  readonly maxVisible: number; // derived from viewMode
  readonly lists: readonly ListSummary[];
  readonly isDropTarget: boolean;
  readonly onDrop: (cardId: string) => void;
  readonly onCardClick: (cardId: string) => void;
  /** Keyboard reschedule: move card left/right by one day */
  readonly onRescheduleMove?: (cardId: string, direction: 'left' | 'right') => void;
  /** Keyboard reschedule: confirm the move */
  readonly onRescheduleConfirm?: (cardId: string) => void;
  /** Keyboard reschedule: cancel the move */
  readonly onRescheduleCancel?: (cardId: string) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function DayCell({
  date,
  isCurrentMonth,
  isToday,
  cards,
  maxVisible,
  lists,
  isDropTarget,
  onDrop,
  onCardClick,
  onRescheduleMove,
  onRescheduleConfirm,
  onRescheduleCancel,
}: DayCellProps): React.ReactElement {
  const sortedCards = useMemo(() => sortCardsForDay(cards, lists), [cards, lists]);

  const visibleCards = sortedCards.slice(0, maxVisible);
  const hiddenCount = Math.max(0, sortedCards.length - maxVisible);

  const listColorMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const list of lists) {
      map.set(list.listId, list.color);
    }
    return map;
  }, [lists]);

  // Extract day-of-month number from date string
  const dayOfMonth = Number(date.slice(8, 10));

  // Build aria-label: full date + card count
  const cardCountLabel = sortedCards.length === 1 ? '1 card' : `${sortedCards.length} cards`;
  const ariaLabel = `${formatDateForAria(date)}, ${cardCountLabel}`;

  // Drop target handlers
  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      const cardId = e.dataTransfer.getData('text/plain');
      if (cardId) {
        onDrop(cardId);
      }
    },
    [onDrop],
  );

  return (
    <div
      role="gridcell"
      aria-label={ariaLabel}
      tabIndex={-1}
      className={`
        relative flex flex-col p-1.5 h-[7rem] border border-border/50 overflow-hidden
        focus-within:ring-2 focus-within:ring-ring transition-colors
        ${isCurrentMonth ? 'bg-card' : 'bg-muted/30'}
        ${isToday ? 'bg-blue-50 dark:bg-blue-900/20 ring-1 ring-blue-300 dark:ring-blue-700' : ''}
        ${isDropTarget ? 'bg-blue-100 dark:bg-blue-800/30 ring-2 ring-blue-400' : ''}
      `}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {/* Day number */}
      <span
        className={`text-xs font-semibold mb-1 shrink-0 ${
          isToday
            ? 'bg-blue-600 text-white w-5 h-5 rounded-full inline-flex items-center justify-center'
            : isCurrentMonth ? 'text-foreground' : 'text-muted-foreground/50'
        }`}
      >
        {dayOfMonth}
      </span>

      {/* Card chips — fixed area */}
      <div className="flex flex-col gap-0.5 min-w-0 flex-1">
        {visibleCards.map((card) => (
          <CardChip
            key={card.cardId}
            card={card}
            listColor={listColorMap.get(card.listId) ?? '#6b7280'}
            onClick={() => onCardClick(card.cardId)}
            onRescheduleMove={onRescheduleMove}
            onRescheduleConfirm={onRescheduleConfirm}
            onRescheduleCancel={onRescheduleCancel}
          />
        ))}
      </div>

      {/* Overflow indicator — positioned at bottom */}
      {hiddenCount > 0 && (
        <div className="shrink-0 mt-0.5">
          <OverflowIndicator
            hiddenCount={hiddenCount}
            allCards={sortedCards}
            lists={lists}
            date={date}
            onCardClick={onCardClick}
          />
        </div>
      )}
    </div>
  );
}
